<template>
    <div>
        <!-- Produktdetailseite (normale Unterseite) -->
        <template v-if="pageType === 'product'">
            <ProductDetailPage
                v-if="productData?.product"
                :product-data="productData"
                :loading="loading"
            />
        </template>

        <!-- Kategorieseite -->
        <div v-else class="m-container py-8">
            <!-- Skeleton Loading -->
            <template v-if="loading">
                <MSkeletonLoader type="text" max-width="300" class="mb-2" />
                <MSkeletonLoader type="heading" class="mb-2" />
                <MSkeletonLoader type="text" max-width="500" class="mb-6" />
                <div class="d-flex ga-2 mb-8">
                    <MSkeletonLoader
                        v-for="n in 6"
                        :key="n"
                        type="chip"
                        :width="80"
                    />
                </div>
                <div class="d-flex ga-3 mb-6">
                    <MSkeletonLoader
                        v-for="n in 3"
                        :key="n"
                        type="button"
                        :width="100"
                    />
                    <span class="m-spacer" />
                    <MSkeletonLoader type="button" :width="180" />
                </div>
                <div class="m-row">
                    <div v-for="n in 8" :key="n" class="m-col m-col-6 m-col-sm-6 m-col-md-3">
                        <MSkeletonLoader type="image, heading, text" />
                    </div>
                </div>
            </template>

            <template v-else-if="category">
                <MBreadcrumbs
                    :items="breadcrumbItems"
                    density="compact"
                    class="breadcrumbs-compact text-caption"
                >
                    <template #divider>
                        <MIcon icon="chevron-right" size="x-small" />
                    </template>
                </MBreadcrumbs>
                <!-- Kategorie Header -->
                <div class="mb-6">
                    <h1 class="titel-h1 font-weight-bold text-secondary">
                        {{ category.titleShort || category.name }}
                    </h1>
                    <p class="text-body-2 text-medium-emphasis mt-1">
                        {{
                            category.titleDescription ||
                            `Entdecke ${category.name} aus hunderten Online-Shops zum besten Preis.`
                        }}
                    </p>
                    <hr class="hr-one mt-4" />
                </div>

                <!-- Unterkategorien Chips -->
                <div
                    v-if="category.children && category.children.length > 0"
                    class="mb-8"
                >
                    <div class="subcategory-chips">
                        <MChip
                            :to="`/${slugPath}`"
                            :variant="!filtersStore.activeSubcategory ? 'flat' : 'outlined'"
                            :class="{ 'chip--active': !filtersStore.activeSubcategory }"
                            class="text-none"
                        >
                            Alle
                        </MChip>
                        <MChip
                            v-for="sub in category.children"
                            :key="sub.id"
                            :to="`/${slugPath}/${sub.slug}`"
                            :variant="filtersStore.activeSubcategory === sub.id ? 'flat' : 'outlined'"
                            :class="{ 'chip--active': filtersStore.activeSubcategory === sub.id }"
                            class="text-none"
                        >
                            {{ sub.name }}
                        </MChip>
                    </div>
                </div>

                <!-- Toolbar: Filter + Sortierung -->
                <div class="filter-toolbar mb-6">
                    <!-- Mobile: Filter-Button + Sort -->
                    <div class="d-flex d-sm-none align-center ga-2">
                        <MBtn
                            variant="outlined"
                            class="text-none flex-1-1"
                            @click="filterSheet = true"
                        >
                            <MIcon start :size="16" icon="tune" />
                            Filter
                            <MChip
                                v-if="filtersStore.activeFilterCount > 0"
                                color="primary"
                                size="x-small"
                                class="ms-1"
                                >{{ filtersStore.activeFilterCount }}</MChip
                            >
                        </MBtn>
                        <MSelect
                            v-model="filtersStore.sortBy"
                            :items="sortOptions"
                            item-title="label"
                            item-value="value"
                            variant="outlined"
                            density="compact"
                            hide-details
                            class="flex-1-1"
                        />
                    </div>

                    <!-- Desktop: Inline Filter -->
                    <div class="d-none d-sm-flex align-center flex-wrap ga-2">
                        <!-- Preis -->
                        <MMenu
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        isPriceFiltered ? 'flat' : 'outlined'
                                    "
                                    :color="
                                        isPriceFiltered ? 'primary' : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    <MIcon start :size="14" icon="currency-eur" />
                                    Preis
                                    <span
                                        v-if="isPriceFiltered"
                                        class="ms-1 filter-btn__range"
                                    >
                                        {{
                                            formatPrice(
                                                filtersStore.priceRange[0],
                                            )
                                        }}
                                        –
                                        {{
                                            formatPrice(
                                                filtersStore.priceRange[1],
                                            )
                                        }}
                                    </span>
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard
                                :min-width="320"
                                rounded="md"
                                :elevation="4"
                                style="overflow: visible"
                            >
                                <MCardTitle
                                    class="text-subtitle-2 font-weight-bold pt-4 px-5 pb-2"
                                >
                                    Preis
                                </MCardTitle>
                                <MCardText
                                    class="px-5 pb-5"
                                    style="overflow: visible"
                                >
                                    <div
                                        class="d-flex justify-space-between align-center mb-4"
                                    >
                                        <MChip
                                            size="small"
                                            variant="tonal"
                                            color="primary"
                                            >{{
                                                formatPrice(
                                                    filtersStore.priceRange[0],
                                                )
                                            }}</MChip
                                        >
                                        <span
                                            class="text-caption text-medium-emphasis mx-2"
                                            >bis</span
                                        >
                                        <MChip
                                            size="small"
                                            variant="tonal"
                                            color="primary"
                                            >{{
                                                formatPrice(
                                                    filtersStore.priceRange[1],
                                                )
                                            }}</MChip
                                        >
                                    </div>
                                    <MRangeSlider
                                        v-model="filtersStore.priceRange"
                                        :min="filtersStore.priceMin"
                                        :max="filtersStore.priceMax"
                                        :step="1"
                                        color="primary"
                                        track-color="primary"
                                        hide-details
                                        class="mt-1 price-slider"
                                    />
                                </MCardText>
                            </MCard>
                        </MMenu>

                        <!-- Händler -->
                        <MMenu
                            v-if="filtersStore.availableMerchants.length"
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        filtersStore.selectedMerchants.length
                                            ? 'flat'
                                            : 'outlined'
                                    "
                                    :color="
                                        filtersStore.selectedMerchants.length
                                            ? 'primary'
                                            : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    <MIcon start :size="14" icon="store" />
                                    Händler
                                    <MChip
                                        v-if="
                                            filtersStore.selectedMerchants
                                                .length
                                        "
                                        size="x-small"
                                        color="white"
                                        class="ms-1"
                                        >{{
                                            filtersStore.selectedMerchants
                                                .length
                                        }}</MChip
                                    >
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard
                                :min-width="240"
                                :max-height="320"
                                class="overflow-y-auto"
                                rounded="md"
                                :elevation="4"
                            >
                                <div
                                    class="pa-4 pb-2 text-subtitle-2 font-weight-bold"
                                >
                                    Händler
                                </div>
                                <MList
                                    density="compact"
                                    select-strategy="classic"
                                >
                                    <MListItem
                                        v-for="merchant in filtersStore.availableMerchants"
                                        :key="merchant"
                                        :value="merchant"
                                        :active="
                                            filtersStore.selectedMerchants.includes(
                                                merchant,
                                            )
                                        "
                                        active-color="primary"
                                        rounded
                                        class="mx-2 mb-1"
                                        @click="toggleMerchant(merchant)"
                                    >
                                        <template #prepend>
                                            <MCheckbox
                                                :model-value="
                                                    filtersStore.selectedMerchants.includes(
                                                        merchant,
                                                    )
                                                "
                                                color="primary"
                                                density="compact"
                                            />
                                        </template>
                                        <template #title>
                                            <span class="text-body-2">{{ merchant }}</span>
                                        </template>
                                    </MListItem>
                                </MList>
                                <div class="pa-3 pt-1">
                                    <MBtn
                                        v-if="
                                            filtersStore.selectedMerchants
                                                .length
                                        "
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        class="text-none"
                                        @click="
                                            filtersStore.selectedMerchants = []
                                        "
                                        >Alle entfernen</MBtn
                                    >
                                </div>
                            </MCard>
                        </MMenu>

                        <!-- Marke -->
                        <MMenu
                            v-if="filtersStore.availableBrands.length"
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        filtersStore.selectedBrands.length
                                            ? 'flat'
                                            : 'outlined'
                                    "
                                    :color="
                                        filtersStore.selectedBrands.length
                                            ? 'primary'
                                            : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    <MIcon start :size="14" icon="tag" />
                                    Marke
                                    <MChip
                                        v-if="
                                            filtersStore.selectedBrands.length
                                        "
                                        size="x-small"
                                        color="white"
                                        class="ms-1"
                                        >{{
                                            filtersStore.selectedBrands.length
                                        }}</MChip
                                    >
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard
                                :min-width="240"
                                :max-height="320"
                                class="overflow-y-auto"
                                rounded="md"
                                :elevation="4"
                            >
                                <div
                                    class="pa-4 pb-2 text-subtitle-2 font-weight-bold"
                                >
                                    Marke
                                </div>
                                <MList
                                    density="compact"
                                    select-strategy="classic"
                                >
                                    <MListItem
                                        v-for="brand in filtersStore.availableBrands"
                                        :key="brand"
                                        :value="brand"
                                        :active="
                                            filtersStore.selectedBrands.includes(
                                                brand,
                                            )
                                        "
                                        active-color="primary"
                                        rounded
                                        class="mx-2 mb-1"
                                        @click="toggleBrand(brand)"
                                    >
                                        <template #prepend>
                                            <MCheckbox
                                                :model-value="
                                                    filtersStore.selectedBrands.includes(
                                                        brand,
                                                    )
                                                "
                                                color="primary"
                                                density="compact"
                                            />
                                        </template>
                                        <template #title>
                                            <span class="text-body-2">{{ brand }}</span>
                                        </template>
                                    </MListItem>
                                </MList>
                                <div class="pa-3 pt-1">
                                    <MBtn
                                        v-if="
                                            filtersStore.selectedBrands.length
                                        "
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        class="text-none"
                                        @click="
                                            filtersStore.selectedBrands = []
                                        "
                                        >Alle entfernen</MBtn
                                    >
                                </div>
                            </MCard>
                        </MMenu>

                        <!-- Farbe -->
                        <MMenu
                            v-if="filtersStore.availableColours.length"
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        filtersStore.selectedColours.length
                                            ? 'flat'
                                            : 'outlined'
                                    "
                                    :color="
                                        filtersStore.selectedColours.length
                                            ? 'primary'
                                            : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    <MIcon start :size="14" icon="palette" />
                                    Farbe
                                    <MChip
                                        v-if="
                                            filtersStore.selectedColours.length
                                        "
                                        size="x-small"
                                        color="white"
                                        class="ms-1"
                                        >{{
                                            filtersStore.selectedColours.length
                                        }}</MChip
                                    >
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard :min-width="280" rounded="md" :elevation="4">
                                <div
                                    class="pa-4 pb-2 text-subtitle-2 font-weight-bold"
                                >
                                    Farbe
                                </div>
                                <div class="pa-4 pt-2 d-flex flex-wrap ga-2">
                                    <MTooltip
                                        v-for="colour in filtersStore.availableColours"
                                        :key="colour"
                                        :text="colour"
                                        location="top"
                                    >
                                        <template #activator="{ props: tp }">
                                            <MBtn
                                                v-bind="tp"
                                                icon
                                                size="small"
                                                :style="{
                                                    background:
                                                        colourToHex(colour),
                                                    border: filtersStore.selectedColours.includes(
                                                        colour,
                                                    )
                                                        ? '3px solid var(--m-primary)'
                                                        : '2px solid rgba(0,0,0,0.15)',
                                                }"
                                                @click="toggleColour(colour)"
                                            />
                                        </template>
                                    </MTooltip>
                                </div>
                                <div class="pa-3 pt-1">
                                    <MBtn
                                        v-if="
                                            filtersStore.selectedColours.length
                                        "
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        class="text-none"
                                        @click="
                                            filtersStore.selectedColours = []
                                        "
                                        >Alle entfernen</MBtn
                                    >
                                </div>
                            </MCard>
                        </MMenu>

                        <!-- Lieferzeit -->
                        <MMenu
                            v-if="
                                filtersStore.availableDeliveryBuckets.length > 1
                            "
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        filtersStore.selectedDeliveryBuckets
                                            .length
                                            ? 'flat'
                                            : 'outlined'
                                    "
                                    :color="
                                        filtersStore.selectedDeliveryBuckets
                                            .length
                                            ? 'primary'
                                            : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    <MIcon start :size="14" icon="truck-delivery-outline" />
                                    Lieferzeit
                                    <MChip
                                        v-if="
                                            filtersStore.selectedDeliveryBuckets
                                                .length
                                        "
                                        size="x-small"
                                        color="white"
                                        class="ms-1"
                                        >{{
                                            filtersStore.selectedDeliveryBuckets
                                                .length
                                        }}</MChip
                                    >
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard :min-width="240" rounded="md" :elevation="4">
                                <div
                                    class="pa-4 pb-2 text-subtitle-2 font-weight-bold"
                                >
                                    Lieferzeit
                                </div>
                                <MList
                                    density="compact"
                                    select-strategy="classic"
                                >
                                    <MListItem
                                        v-for="bucket in filtersStore.availableDeliveryBuckets"
                                        :key="bucket"
                                        :active="
                                            filtersStore.selectedDeliveryBuckets.includes(
                                                bucket,
                                            )
                                        "
                                        active-color="primary"
                                        rounded
                                        class="mx-2 mb-1"
                                        @click="toggleDeliveryBucket(bucket)"
                                    >
                                        <template #prepend>
                                            <MCheckbox
                                                :model-value="
                                                    filtersStore.selectedDeliveryBuckets.includes(
                                                        bucket,
                                                    )
                                                "
                                                color="primary"
                                                density="compact"
                                            />
                                        </template>
                                        <template #title>
                                            <span class="text-body-2">{{ bucket }}</span>
                                        </template>
                                    </MListItem>
                                </MList>
                                <div class="pa-3 pt-1">
                                    <MBtn
                                        v-if="
                                            filtersStore.selectedDeliveryBuckets
                                                .length
                                        "
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        class="text-none"
                                        @click="
                                            filtersStore.selectedDeliveryBuckets =
                                                []
                                        "
                                        >Alle entfernen</MBtn
                                    >
                                </div>
                            </MCard>
                        </MMenu>

                        <!-- Dynamische Spec-Filter -->
                        <MMenu
                            v-for="(
                                values, label
                            ) in filtersStore.availableSpecifications"
                            :key="label"
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        (
                                            filtersStore.selectedSpecifications[
                                                label
                                            ] || []
                                        ).length
                                            ? 'flat'
                                            : 'outlined'
                                    "
                                    :color="
                                        (
                                            filtersStore.selectedSpecifications[
                                                label
                                            ] || []
                                        ).length
                                            ? 'primary'
                                            : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    {{ label }}
                                    <MChip
                                        v-if="
                                            (
                                                filtersStore
                                                    .selectedSpecifications[
                                                    label
                                                ] || []
                                            ).length
                                        "
                                        size="x-small"
                                        color="white"
                                        class="ms-1"
                                        >{{
                                            (
                                                filtersStore
                                                    .selectedSpecifications[
                                                    label
                                                ] || []
                                            ).length
                                        }}</MChip
                                    >
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard
                                :min-width="240"
                                :max-height="320"
                                class="overflow-y-auto"
                                rounded="md"
                                :elevation="4"
                            >
                                <div
                                    class="pa-4 pb-2 text-subtitle-2 font-weight-bold"
                                >
                                    {{ label }}
                                </div>
                                <MList
                                    density="compact"
                                    select-strategy="classic"
                                >
                                    <MListItem
                                        v-for="val in values"
                                        :key="val"
                                        :active="
                                            (
                                                filtersStore
                                                    .selectedSpecifications[
                                                    label
                                                ] || []
                                            ).includes(val)
                                        "
                                        active-color="primary"
                                        rounded
                                        class="mx-2 mb-1"
                                        @click="toggleSpecification(label, val)"
                                    >
                                        <template #prepend>
                                            <MCheckbox
                                                :model-value="
                                                    (
                                                        filtersStore
                                                            .selectedSpecifications[
                                                            label
                                                        ] || []
                                                    ).includes(val)
                                                "
                                                color="primary"
                                                density="compact"
                                            />
                                        </template>
                                        <template #title>
                                            <span class="text-body-2">{{ val }}</span>
                                        </template>
                                    </MListItem>
                                </MList>
                                <div class="pa-3 pt-1">
                                    <MBtn
                                        v-if="
                                            (
                                                filtersStore
                                                    .selectedSpecifications[
                                                    label
                                                ] || []
                                            ).length
                                        "
                                        variant="text"
                                        size="small"
                                        color="primary"
                                        class="text-none"
                                        @click="
                                            filtersStore.selectedSpecifications[
                                                label
                                            ] = []
                                        "
                                        >Alle entfernen</MBtn
                                    >
                                </div>
                            </MCard>
                        </MMenu>

                        <!-- Sprint F: Dynamische Attribute-Filter aus category.filters -->
                        <MMenu
                            v-for="key in categoryFiltersStore.definition.enabled"
                            :key="`attr-${key}`"
                            :close-on-content-click="false"
                            location="bottom start"
                        >
                            <template #activator="{ props, isActive }">
                                <MBtn
                                    v-bind="props"
                                    :variant="
                                        (
                                            categoryFiltersStore.selected[key] || []
                                        ).length
                                            ? 'flat'
                                            : 'outlined'
                                    "
                                    :color="
                                        (
                                            categoryFiltersStore.selected[key] || []
                                        ).length
                                            ? 'primary'
                                            : undefined
                                    "
                                    size="small"
                                    class="text-none filter-btn"
                                >
                                    {{ attributeLabel(key) }}
                                    <MChip
                                        v-if="
                                            (
                                                categoryFiltersStore.selected[key] || []
                                            ).length
                                        "
                                        size="x-small"
                                        color="white"
                                        class="ms-1"
                                        >{{
                                            (
                                                categoryFiltersStore.selected[key] || []
                                            ).length
                                        }}</MChip
                                    >
                                    <MIcon end :size="14" :icon="
                                        isActive
                                            ? 'chevron-up'
                                            : 'chevron-down'
                                    " />
                                </MBtn>
                            </template>
                            <MCard
                                :min-width="240"
                                :max-height="320"
                                class="overflow-y-auto"
                                rounded="md"
                                :elevation="4"
                            >
                                <div
                                    class="pa-4 pb-2 text-subtitle-2 font-weight-bold"
                                >
                                    {{ attributeLabel(key) }}
                                </div>
                                <MList
                                    density="compact"
                                    select-strategy="classic"
                                >
                                    <MListItem
                                        v-for="val in categoryFiltersStore.definition.values[key] || []"
                                        :key="val"
                                        :active="
                                            (
                                                categoryFiltersStore.selected[key] || []
                                            ).includes(val)
                                        "
                                        active-color="primary"
                                        rounded
                                        class="mx-2 mb-1"
                                        @click="categoryFiltersStore.toggle(key, val)"
                                    >
                                        <template #prepend>
                                            <MCheckbox
                                                :model-value="
                                                    (
                                                        categoryFiltersStore.selected[key] || []
                                                    ).includes(val)
                                                "
                                                color="primary"
                                                density="compact"
                                            />
                                        </template>
                                        <template #title>
                                            <span class="text-body-2">{{ val }}</span>
                                        </template>
                                    </MListItem>
                                </MList>
                            </MCard>
                        </MMenu>

                        <!-- Reset -->
                        <MBtn
                            v-if="filtersStore.activeFilterCount > 0 || categoryFiltersStore.activeCount > 0"
                            variant="text"
                            size="small"
                            color="secondary"
                            class="text-none"
                            @click="resetAllFilters"
                        >
                            <MIcon start :size="14" icon="close" />
                            Zurücksetzen
                        </MBtn>

                        <span class="m-spacer" />

                        <MSelect
                            v-model="filtersStore.sortBy"
                            :items="sortOptions"
                            item-title="label"
                            item-value="value"
                            variant="outlined"
                            density="compact"
                            hide-details
                            style="max-width: 190px"
                        />
                        <div class="view-toggle" role="radiogroup" aria-label="Ansicht">
                            <button
                                class="view-toggle__btn"
                                :class="{ 'view-toggle__btn--active': filtersStore.viewMode === 'grid' }"
                                role="radio"
                                :aria-checked="filtersStore.viewMode === 'grid'"
                                aria-label="Rasteransicht"
                                @click="filtersStore.viewMode = 'grid'"
                            >
                                <MIcon size="small" icon="view-grid-outline" />
                            </button>
                            <button
                                class="view-toggle__btn"
                                :class="{ 'view-toggle__btn--active': filtersStore.viewMode === 'list' }"
                                role="radio"
                                :aria-checked="filtersStore.viewMode === 'list'"
                                aria-label="Listenansicht"
                                @click="filtersStore.viewMode = 'list'"
                            >
                                <MIcon size="small" icon="format-list-bulleted" />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Mobile Filter Bottom Sheet -->
                <MBottomSheet v-model="filterSheet" scrollable>
                    <MCard rounded="t">
                        <div
                            class="d-flex align-center justify-space-between pa-4 pb-2"
                        >
                            <span class="text-subtitle-1 font-weight-bold"
                                >Filter</span
                            >
                            <MBtn
                                icon
                                variant="text"
                                size="small"
                                @click="filterSheet = false"
                            >
                                <MIcon icon="close" />
                            </MBtn>
                        </div>
                        <hr class="m-divider" />
                        <MCardText class="pa-4">
                            <!-- Preis -->
                            <div class="mb-6">
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    Preis
                                </div>
                                <div
                                    class="d-flex justify-space-between align-center mb-3"
                                >
                                    <MChip
                                        size="small"
                                        variant="tonal"
                                        color="primary"
                                        >{{
                                            formatPrice(
                                                filtersStore.priceRange[0],
                                            )
                                        }}</MChip
                                    >
                                    <span
                                        class="text-caption text-medium-emphasis"
                                        >bis</span
                                    >
                                    <MChip
                                        size="small"
                                        variant="tonal"
                                        color="primary"
                                        >{{
                                            formatPrice(
                                                filtersStore.priceRange[1],
                                            )
                                        }}</MChip
                                    >
                                </div>
                                <MRangeSlider
                                    v-model="filtersStore.priceRange"
                                    :min="filtersStore.priceMin"
                                    :max="filtersStore.priceMax"
                                    :step="1"
                                    color="primary"
                                    track-color="primary"
                                    hide-details
                                    class="mx-1 price-slider"
                                />
                            </div>

                            <!-- Händler -->
                            <div
                                v-if="filtersStore.availableMerchants.length"
                                class="mb-6"
                            >
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    Händler
                                </div>
                                <div class="d-flex flex-wrap ga-2">
                                    <MChip
                                        v-for="merchant in filtersStore.availableMerchants"
                                        :key="merchant"
                                        :color="
                                            filtersStore.selectedMerchants.includes(
                                                merchant,
                                            )
                                                ? 'primary'
                                                : undefined
                                        "
                                        :variant="
                                            filtersStore.selectedMerchants.includes(
                                                merchant,
                                            )
                                                ? 'flat'
                                                : 'outlined'
                                        "
                                        size="small"
                                        class="text-none"
                                        @click="toggleMerchant(merchant)"
                                        >{{ merchant }}</MChip
                                    >
                                </div>
                            </div>

                            <!-- Marke -->
                            <div
                                v-if="filtersStore.availableBrands.length"
                                class="mb-6"
                            >
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    Marke
                                </div>
                                <div class="d-flex flex-wrap ga-2">
                                    <MChip
                                        v-for="brand in filtersStore.availableBrands"
                                        :key="brand"
                                        :color="
                                            filtersStore.selectedBrands.includes(
                                                brand,
                                            )
                                                ? 'primary'
                                                : undefined
                                        "
                                        :variant="
                                            filtersStore.selectedBrands.includes(
                                                brand,
                                            )
                                                ? 'flat'
                                                : 'outlined'
                                        "
                                        size="small"
                                        class="text-none"
                                        @click="toggleBrand(brand)"
                                        >{{ brand }}</MChip
                                    >
                                </div>
                            </div>

                            <!-- Farbe -->
                            <div
                                v-if="filtersStore.availableColours.length"
                                class="mb-6"
                            >
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    Farbe
                                </div>
                                <div class="d-flex flex-wrap ga-2">
                                    <MBtn
                                        v-for="colour in filtersStore.availableColours"
                                        :key="colour"
                                        icon
                                        size="small"
                                        :style="{
                                            background: colourToHex(colour),
                                            border: filtersStore.selectedColours.includes(
                                                colour,
                                            )
                                                ? '3px solid var(--m-primary)'
                                                : '2px solid rgba(0,0,0,0.15)',
                                        }"
                                        :title="colour"
                                        :aria-label="`Farbe ${colour} ${filtersStore.selectedColours.includes(colour) ? 'entfernen' : 'auswählen'}`"
                                        :aria-pressed="filtersStore.selectedColours.includes(colour)"
                                        @click="toggleColour(colour)"
                                    />
                                </div>
                            </div>

                            <!-- Lieferzeit -->
                            <div
                                v-if="
                                    filtersStore.availableDeliveryBuckets
                                        .length > 1
                                "
                                class="mb-6"
                            >
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    Lieferzeit
                                </div>
                                <div class="d-flex flex-wrap ga-2">
                                    <MChip
                                        v-for="bucket in filtersStore.availableDeliveryBuckets"
                                        :key="bucket"
                                        :color="
                                            filtersStore.selectedDeliveryBuckets.includes(
                                                bucket,
                                            )
                                                ? 'primary'
                                                : undefined
                                        "
                                        :variant="
                                            filtersStore.selectedDeliveryBuckets.includes(
                                                bucket,
                                            )
                                                ? 'flat'
                                                : 'outlined'
                                        "
                                        size="small"
                                        class="text-none"
                                        @click="toggleDeliveryBucket(bucket)"
                                        >{{ bucket }}</MChip
                                    >
                                </div>
                            </div>

                            <!-- Dynamische Spec-Filter -->
                            <div
                                v-for="(
                                    values, label
                                ) in filtersStore.availableSpecifications"
                                :key="label"
                                class="mb-6"
                            >
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    {{ label }}
                                </div>
                                <div class="d-flex flex-wrap ga-2">
                                    <MChip
                                        v-for="val in values"
                                        :key="val"
                                        :color="
                                            (
                                                filtersStore
                                                    .selectedSpecifications[
                                                    label
                                                ] || []
                                            ).includes(val)
                                                ? 'primary'
                                                : undefined
                                        "
                                        :variant="
                                            (
                                                filtersStore
                                                    .selectedSpecifications[
                                                    label
                                                ] || []
                                            ).includes(val)
                                                ? 'flat'
                                                : 'outlined'
                                        "
                                        size="small"
                                        class="text-none"
                                        @click="toggleSpecification(label, val)"
                                        >{{ val }}</MChip
                                    >
                                </div>
                            </div>

                            <!-- Sprint F: Dynamische Attribute-Filter (Mobile) -->
                            <div
                                v-for="key in categoryFiltersStore.definition.enabled"
                                :key="`m-attr-${key}`"
                                class="mb-6"
                            >
                                <div
                                    class="text-subtitle-2 font-weight-bold mb-3"
                                >
                                    {{ attributeLabel(key) }}
                                </div>
                                <div class="d-flex flex-wrap ga-2">
                                    <MChip
                                        v-for="val in categoryFiltersStore.definition.values[key] || []"
                                        :key="val"
                                        :color="
                                            (categoryFiltersStore.selected[key] || []).includes(val)
                                                ? 'primary'
                                                : undefined
                                        "
                                        :variant="
                                            (categoryFiltersStore.selected[key] || []).includes(val)
                                                ? 'flat'
                                                : 'outlined'
                                        "
                                        size="small"
                                        class="text-none"
                                        @click="categoryFiltersStore.toggle(key, val)"
                                        >{{ val }}</MChip
                                    >
                                </div>
                            </div>
                        </MCardText>
                        <hr class="m-divider" />
                        <div class="pa-4 d-flex ga-3">
                            <MBtn
                                v-if="filtersStore.activeFilterCount > 0 || categoryFiltersStore.activeCount > 0"
                                variant="outlined"
                                class="text-none"
                                @click="resetAllFilters"
                                >Zurücksetzen</MBtn
                            >
                            <MBtn
                                color="primary"
                                variant="flat"
                                class="text-none flex-1-1"
                                @click="filterSheet = false"
                            >
                                Ergebnisse anzeigen ({{
                                    filtersStore.filteredProducts.length
                                }})
                            </MBtn>
                        </div>
                    </MCard>
                </MBottomSheet>

                <!-- Product Skeletons — sofort beim Laden UND vor dem ersten
                     Fetch (Navigation), nie Leer-/Empty-Blitz -->
                <div v-if="productsStore.loading || !productsStore.hasFetched" class="m-row">
                    <div v-for="n in 8" :key="n" class="m-col m-col-6 m-col-sm-6 m-col-md-3">
                        <MSkeletonLoader type="image, heading, text" />
                    </div>
                </div>

                <!-- Products -->
                <template v-else-if="filtersStore.filteredProducts.length > 0">
                    <div class="m-row">
                        <div
                            v-for="product in filtersStore.filteredProducts"
                            :key="product.id"
                            class="m-col m-col-6"
                            :class="[
                                filtersStore.viewMode === 'list' ? 'm-col-sm-12 m-col-md-12' : 'm-col-sm-6 m-col-md-3'
                            ]"
                        >
                            <div :id="`product-${product.id}`">
                                <ProductCard
                                    :product="product"
                                    :view-mode="filtersStore.viewMode === 'grid' ? 'grid' : 'list'"
                                />
                            </div>
                        </div>
                    </div>
                </template>

                <InfiniteScroll
                    v-if="!productsStore.loading && productsStore.hasFetched"
                    :loading="productsStore.loadingMore"
                    :has-more="productsStore.hasMore"
                    @load="productsStore.fetchMoreProducts()"
                >
                    WEITERE PRODUKTE LADEN
                    <span style="opacity: 0.8; font-weight: 400;">
                        ({{ filtersStore.filteredProducts.length }} VON
                        {{ productsStore.total.toLocaleString("de-DE") }})
                    </span>
                </InfiniteScroll>

                <!-- Empty State — erst NACH abgeschlossenem Fetch (kein Blitz) -->
                <div
                    v-if="
                        productsStore.hasFetched &&
                        !productsStore.loading &&
                        !productsStore.hasMore &&
                        filtersStore.filteredProducts.length === 0
                    "
                    class="text-center py-16"
                >
                    <MIcon :size="64" color="grey-lighten-2" class="mb-4" icon="package-variant" />
                    <h3 class="titel-h3 text-medium-emphasis mb-2">
                        <template v-if="filtersStore.activeFilterCount > 0">
                            Keine Treffer mit aktuellen Filtern
                        </template>
                        <template v-else>
                            Noch keine Produkte verfügbar
                        </template>
                    </h3>
                    <p class="text-body-2 text-medium-emphasis mb-6">
                        <template v-if="filtersStore.activeFilterCount > 0">
                            Lockere die Filter oder schau dir verwandte Kategorien an.
                        </template>
                        <template v-else>
                            Produkte für "{{ activeSubcategoryName || category.name }}"
                            werden bald aus unseren Partnershops geladen.
                        </template>
                    </p>
                    <div class="d-flex flex-wrap justify-center ga-2">
                        <MBtn
                            v-if="filtersStore.activeFilterCount > 0"
                            variant="outlined"
                            color="primary"
                            class="text-none"
                            @click="filtersStore.resetFilters()"
                        >
                            Filter zurücksetzen
                        </MBtn>
                        <MBtn
                            variant="outlined"
                            color="primary"
                            class="text-none"
                            to="/kategorien"
                        >
                            <MIcon start :size="16" icon="view-grid" />
                            Alle Kategorien
                        </MBtn>
                    </div>
                </div>

                <!-- Kategorie-Beschreibung (SEO Content) -->
                <section
                    v-if="categoryContentHtml"
                    class="category-description mt-12"
                >
                    <div class="category-description__inner">
                        <h2 class="category-description__title">
                            Alles über {{ category.name }}
                        </h2>
                        <div
                            class="category-description__body"
                            v-html="categoryContentHtml"
                        />
                    </div>
                </section>
            </template>

            <!-- Nicht gefunden -->
            <div v-else class="text-center py-16">
                <MIcon :size="64" color="grey-lighten-2" class="mb-4" icon="magnify-close" />
                <h2 class="titel-h2 text-medium-emphasis mb-2">
                    Seite nicht gefunden
                </h2>
                <p class="text-body-2 text-medium-emphasis mb-6">
                    Die gesuchte Seite existiert leider nicht.
                </p>
                <MBtn
                    color="primary"
                    variant="flat"
                    to="/"
                    class="text-none"
                >
                    Zur Startseite
                </MBtn>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { CategoryItem } from "~/stores/categories";
import { useProductsStore } from "~/stores/products";
import { useFiltersStore, sortOptions } from "~/stores/filters";
import { useCategoryFiltersStore } from "~/stores/categoryFilters";
import { useAttributeFilterUrlState } from "~/composables/useAttributeFilterUrlState";
import { getProductUrl } from "~/composables/useProductUrl";
import { priceValidUntil } from "~/utils/seo";
import { colourToHex } from "~/utils/colour";

const route = useRoute();
const slugPath = computed(() => {
    const params = route.params.slug;
    if (Array.isArray(params)) return params.join("/");
    return params || "";
});

const segments = computed(() => slugPath.value.split("/").filter(Boolean));

const productsStore = useProductsStore();
const filtersStore = useFiltersStore();
const categoryFiltersStore = useCategoryFiltersStore();
useAttributeFilterUrlState();
const api = useApi();

/** Key → Label für Attribute-Filter-Buttons. Fallback: Capitalized Key. */
const ATTRIBUTE_LABELS: Record<string, string> = {
    material: "Material",
    color: "Farbe",
    colour: "Farbe",
    style: "Stil",
    shape: "Form",
    size: "Größe",
    room: "Raum",
    finish: "Finish",
    pattern: "Muster",
};
function attributeLabel(key: string): string {
    return (
        ATTRIBUTE_LABELS[key.toLowerCase()] ??
        key.charAt(0).toUpperCase() + key.slice(1)
    );
}

function resetAllFilters() {
    filtersStore.resetFilters();
    categoryFiltersStore.reset();
}

// Resolve category and product in parallel, then load products during SSR
const { data: resolvedData, status: resolvedStatus } = await useAsyncData(
    `resolve-v3-${slugPath.value}`,
    async () => {
        const segs = slugPath.value.split("/").filter(Boolean);

        // Step 1: Category lookup
        const categoryResult = await api<any>("/api/categories/resolve", {
            query: { path: slugPath.value },
        }).catch((e: any) =>
            e?.statusCode === 404 ? null : Promise.reject(e),
        );

        // Step 2: Product lookup only if category not found AND 2+ segments
        const productResult =
            !categoryResult?.category && segs.length >= 2
                ? await api<any>("/api/products/resolve", {
                      query: {
                          slug: segs[segs.length - 1],
                          categoryPath: segs.slice(0, -1).join("/"),
                      },
                  }).catch((e: any) =>
                      e?.statusCode === 404 ? null : Promise.reject(e),
                  )
                : null;

        return {
            category: categoryResult?.category ?? null,
            product: productResult?.product ?? null,
        };
    },
    { watch: [slugPath] },
);

const categoryData = computed(() =>
    resolvedData.value?.category
        ? { category: resolvedData.value.category }
        : null,
);
const productData = computed(() =>
    resolvedData.value?.product
        ? { product: resolvedData.value.product }
        : null,
);
const categoryStatus = resolvedStatus;
const productStatus = resolvedStatus;

const pageType = computed(() => {
    if (categoryData.value?.category) return "category";
    if (productData.value?.product) return "product";
    return "not-found";
});


const filterSheet = ref(false);

const isPriceFiltered = computed(
    () =>
        filtersStore.priceRange[0] > filtersStore.priceMin ||
        filtersStore.priceRange[1] < filtersStore.priceMax,
);

const toggleMerchant = (merchant: string) => {
    const idx = filtersStore.selectedMerchants.indexOf(merchant);
    if (idx === -1) filtersStore.selectedMerchants.push(merchant);
    else filtersStore.selectedMerchants.splice(idx, 1);
};

const toggleBrand = (brand: string) => {
    const idx = filtersStore.selectedBrands.indexOf(brand);
    if (idx === -1) filtersStore.selectedBrands.push(brand);
    else filtersStore.selectedBrands.splice(idx, 1);
};

const toggleColour = (c: string) => {
    const idx = filtersStore.selectedColours.indexOf(c);
    if (idx === -1) filtersStore.selectedColours.push(c);
    else filtersStore.selectedColours.splice(idx, 1);
};

const toggleDeliveryBucket = (b: string) => {
    const idx = filtersStore.selectedDeliveryBuckets.indexOf(b);
    if (idx === -1) filtersStore.selectedDeliveryBuckets.push(b);
    else filtersStore.selectedDeliveryBuckets.splice(idx, 1);
};

const toggleSpecification = (label: string, val: string) => {
    if (!filtersStore.selectedSpecifications[label])
        filtersStore.selectedSpecifications[label] = [];
    const arr = filtersStore.selectedSpecifications[label];
    const idx = arr.indexOf(val);
    if (idx === -1) arr.push(val);
    else arr.splice(idx, 1);
};

const loading = computed(
    () =>
        categoryStatus.value === "pending" || productStatus.value === "pending",
);
const category = computed<CategoryItem | null>(
    () => categoryData.value?.category || null,
);

// Category breadcrumbs
const breadcrumbItems = computed(() => {
    const crumbs: Array<{ title: string; to?: string; disabled?: boolean }> = [
        { title: "Home", to: "/" },
    ];
    if (category.value) {
        if (category.value.parent) {
            const p = category.value.parent;
            if (p.parent) {
                crumbs.push({ title: p.parent.name, to: `/${p.parent.slug}` });
                crumbs.push({
                    title: p.name,
                    to: `/${p.parent.slug}/${p.slug}`,
                });
            } else {
                crumbs.push({ title: p.name, to: `/${p.slug}` });
            }
        }
        crumbs.push({ title: category.value.name });
    }
    return crumbs;
});

// Active subcategory name
const activeSubcategoryName = computed(() => {
    if (!filtersStore.activeSubcategory || !category.value?.children)
        return null;
    const sub = category.value.children.find(
        (c) => c.id === filtersStore.activeSubcategory,
    );
    return sub?.name || null;
});

// Category content: JSON components to HTML
const categoryContentHtml = computed(() => {
    const content = category.value?.content as any;
    if (!content) return null;
    if (typeof content === "string") return content;
    if (content.components && content.components.length > 0) {
        return content.components
            .map((c: any) => {
                if (c.type === "text") return `<p>${c.data?.text || ""}</p>`;
                if (c.type === "heading")
                    return `<h3>${c.data?.text || ""}</h3>`;
                return "";
            })
            .join("");
    }
    return null;
});

// Sync products from productsStore → filtersStore
watch(() => productsStore.products, (prods) => {
    filtersStore.setProducts([...prods] as any[]);
}, { immediate: true });

const formatPrice = (price: number) => {
    return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
    }).format(price);
};

// Fetch products from API via store
const loadProducts = async (categoryId?: string) => {
    await productsStore.fetchProducts({
        categoryId,
        attributes: categoryFiltersStore.activeCount > 0
            ? categoryFiltersStore.selected
            : undefined,
    });
};

// Sprint F: Filter-Definition pro Kategorie-Slug laden
watch(
    () => category.value?.slug,
    (slug) => {
        if (slug) void categoryFiltersStore.load(slug);
    },
    { immediate: true },
);

// Client: load products on mount
onMounted(() => {
    if (category.value) {
        loadProducts(category.value.id);
    }
});

// Client: reload when navigating between categories
watch(category, (cat, oldCat) => {
    if (!cat) return;
    if (!oldCat || cat.id !== oldCat.id) {
        loadProducts(cat.id);
    }
});

// Sprint F: Re-fetch wenn Attribute-Filter sich ändern (debounced gegen Flackern)
let attrReloadTimer: ReturnType<typeof setTimeout> | null = null;
watch(
    () => categoryFiltersStore.selected,
    () => {
        if (!category.value) return;
        if (attrReloadTimer) clearTimeout(attrReloadTimer);
        attrReloadTimer = setTimeout(() => {
            loadProducts(category.value!.id);
        }, 150);
    },
    { deep: true },
);

// Handle 404 — only if neither category nor product found
watch(
    [resolvedStatus, resolvedData],
    ([status, data]) => {
        if (status !== "pending") {
            if (!data?.category && !data?.product) {
                showError({
                    statusCode: 404,
                    statusMessage: "Seite nicht gefunden",
                });
            }
        }
    },
    { immediate: true },
);

// React to route changes — reset filters + SOFORT Skeletons zeigen
// (markUnfetched läuft synchron, bevor der async Resolve+Fetch startet → kein
// Leer-/Empty-Blitz beim Seitenwechsel).
watch(slugPath, () => {
    productsStore.markUnfetched();
    filtersStore.resetFilters();
});

// SEO — category + product
const productForSeo = computed(() => productData.value?.product || null);

const productSeoTitle = computed(() => {
    const p = productForSeo.value;
    if (!p) return undefined;
    const parts = [p.name];
    if (p.brand && !p.name.toLowerCase().includes(p.brand.toLowerCase())) parts.unshift(p.brand);
    const price = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(p.price);
    parts.push(`ab ${price}`);
    return `${parts.join(" ")} | Moebix.de`;
});

const productSeoDesc = computed(() => {
    const p = productForSeo.value;
    if (!p) return undefined;
    const price = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(p.price);
    const parts = [`${p.name} günstig kaufen`, `für ${price}`];
    if (p.brand) parts.push(`von ${p.brand}`);
    parts.push("Preisvergleich, Preisentwicklung & Bewertungen bei Moebix.de");
    return parts.join(" — ");
});

const productCanonicalUrl = computed(() => {
    const p = productForSeo.value;
    if (!p) return undefined;
    return `https://www.moebix.de${getProductUrl(p)}`;
});

useSeoMeta({
    title: computed(() => {
        if (pageType.value === "product") return productSeoTitle.value;
        if (pageType.value === "category") {
            return (
                category.value?.titleShort ||
                category.value?.name ||
                "Kategorie"
            );
        }
        return undefined;
    }),
    description: computed(() => {
        if (pageType.value === "product") return productSeoDesc.value;
        if (pageType.value === "category" && category.value) {
            return (
                category.value.titleDescription ||
                `${category.value.name} online kaufen — Preisvergleich aus hunderten Shops. Finde die besten Angebote bei Moebix.de`
            );
        }
        return undefined;
    }),
    ogTitle: computed(() => {
        if (pageType.value === "product" && productForSeo.value) {
            return productForSeo.value.name;
        }
        if (pageType.value === "category" && category.value) {
            return `${category.value.name} — Moebix.de`;
        }
        return undefined;
    }),
    ogDescription: computed(() => {
        if (pageType.value === "product") return productSeoDesc.value;
        if (pageType.value === "category" && category.value) {
            return category.value.titleDescription || `${category.value.name} — Preisvergleich bei Moebix.de`;
        }
        return undefined;
    }),
    ogImage: computed(() => {
        if (pageType.value === "product" && productForSeo.value?.image) {
            return productForSeo.value.image;
        }
        if (pageType.value === "category" && productsStore.products?.[0]?.image) {
            return productsStore.products[0].image;
        }
        return '/moebix_og_sticker.jpg';
    }),
    ogType: computed(() => {
        if (pageType.value === "product") return "website" as const;
        if (pageType.value === "category") return "website" as const;
        return undefined;
    }),
    ogUrl: computed(() => {
        if (pageType.value === "product") return productCanonicalUrl.value;
        return undefined;
    }),
    twitterCard: computed(() => {
        if (pageType.value === "product") return "summary_large_image" as const;
        return undefined;
    }),
    twitterTitle: computed(() => {
        if (pageType.value === "product" && productForSeo.value) return productForSeo.value.name;
        return undefined;
    }),
    twitterDescription: computed(() => {
        if (pageType.value === "product") return productSeoDesc.value;
        return undefined;
    }),
    twitterImage: computed(() => {
        if (pageType.value === "product" && productForSeo.value?.image) return productForSeo.value.image;
        return undefined;
    }),
});

// Structured data (JSON-LD) + canonical — category + product
useHead({
    link: computed(() => {
        if (pageType.value === "product" && productCanonicalUrl.value) {
            return [{ rel: "canonical", href: productCanonicalUrl.value }];
        }
        if (pageType.value === "category" && category.value) {
            const catPath = slugPath.value;
            return [{ rel: "canonical", href: `https://www.moebix.de/${catPath}` }];
        }
        return [];
    }),
    meta: computed(() => {
        if (pageType.value === "product" && productForSeo.value) {
            return [
                { property: "product:price:amount", content: String(productForSeo.value.price || "") },
                { property: "product:price:currency", content: "EUR" },
                { property: "product:availability", content: productForSeo.value.inStock !== false ? "in stock" : "out of stock" },
            ];
        }
        return [];
    }),
    script: computed(() => {
        const scripts: any[] = [];

        // Category BreadcrumbList
        if (pageType.value === "category" && category.value) {
            const items: any[] = [
                { "@type": "ListItem", position: 1, name: "Moebix.de", item: "https://www.moebix.de/" },
            ];
            let pos = 2;
            if (category.value.parent) {
                const p = category.value.parent;
                if (p.parent) {
                    items.push({ "@type": "ListItem", position: pos++, name: p.parent.name, item: `https://www.moebix.de/${p.parent.slug}` });
                    items.push({ "@type": "ListItem", position: pos++, name: p.name, item: `https://www.moebix.de/${p.parent.slug}/${p.slug}` });
                } else {
                    items.push({ "@type": "ListItem", position: pos++, name: p.name, item: `https://www.moebix.de/${p.slug}` });
                }
            }
            items.push({ "@type": "ListItem", position: pos, name: category.value.name });
            scripts.push({
                type: "application/ld+json",
                innerHTML: JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items }),
            });

            // CollectionPage JSON-LD — angereichert mit einer ItemList der
            // sichtbaren Produkte (hilft Google, die Seite als Produktliste zu
            // verstehen → bessere Kategorie-Rich-Results).
            const catPath = slugPath.value;
            const listItems = (productsStore.products || [])
                .slice(0, 24)
                .map((pr: any, i: number) => ({
                    "@type": "ListItem",
                    position: i + 1,
                    url: `https://www.moebix.de${getProductUrl(pr)}`,
                    name: pr.name,
                }));
            scripts.push({
                type: "application/ld+json",
                innerHTML: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "CollectionPage",
                    name: category.value.titleShort || category.value.name,
                    description: category.value.titleDescription || `${category.value.name} im Preisvergleich`,
                    url: `https://www.moebix.de/${catPath}`,
                    numberOfItems: productsStore.total || undefined,
                    mainEntity: listItems.length
                        ? { "@type": "ItemList", numberOfItems: productsStore.total || listItems.length, itemListElement: listItems }
                        : undefined,
                }),
            });
        }

        // Product JSON-LD + BreadcrumbList
        if (pageType.value === "product" && productForSeo.value) {
            const p = productForSeo.value;
            const images = [p.image, ...(p.images || [])].filter(Boolean);

            scripts.push({
                type: "application/ld+json",
                innerHTML: JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Product",
                    name: p.brand ? `${p.brand} ${p.name}` : p.name,
                    image: images.length > 0 ? images : undefined,
                    description: p.description || `${p.name} günstig kaufen bei Moebix.de`,
                    brand: p.brand ? { "@type": "Brand", name: p.brand } : undefined,
                    gtin13: p.ean || undefined,
                    sku: p.id,
                    offers: {
                        "@type": "Offer",
                        url: p.affiliateUrl,
                        priceCurrency: "EUR",
                        price: p.price,
                        priceValidUntil: priceValidUntil(),
                        availability: p.inStock !== false ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
                        itemCondition: "https://schema.org/NewCondition",
                        seller: { "@type": "Organization", name: p.merchantName },
                    },
                }),
            });

            // Product BreadcrumbList
            const cat = p.category;
            const crumbs: any[] = [{ "@type": "ListItem", position: 1, name: "Moebix.de", item: "https://www.moebix.de/" }];
            let bpos = 2;
            if (cat) {
                if (cat.parent?.parent) {
                    crumbs.push({ "@type": "ListItem", position: bpos++, name: cat.parent.parent.name, item: `https://www.moebix.de/${cat.parent.parent.slug}` });
                    crumbs.push({ "@type": "ListItem", position: bpos++, name: cat.parent.name, item: `https://www.moebix.de/${cat.parent.parent.slug}/${cat.parent.slug}` });
                } else if (cat.parent) {
                    crumbs.push({ "@type": "ListItem", position: bpos++, name: cat.parent.name, item: `https://www.moebix.de/${cat.parent.slug}` });
                }
                const catParts: string[] = [];
                if (cat.parent?.parent?.slug) catParts.push(cat.parent.parent.slug);
                if (cat.parent?.slug) catParts.push(cat.parent.slug);
                catParts.push(cat.slug);
                crumbs.push({ "@type": "ListItem", position: bpos++, name: cat.name, item: `https://www.moebix.de/${catParts.join("/")}` });
            }
            crumbs.push({ "@type": "ListItem", position: bpos, name: p.name });
            scripts.push({
                type: "application/ld+json",
                innerHTML: JSON.stringify({ "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: crumbs }),
            });
        }

        return scripts;
    }),
});
</script>

<style scoped>
.category-header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    padding-bottom: 24px;
}

.category-description {
    border-top: 1px solid var(--m-border-light, rgba(0,0,0,0.08));
    padding-top: 32px;
}
.category-description__inner {
    background: var(--m-surface-variant, #f5f5f5);
    border-radius: var(--m-radius, 4px);
    padding: 28px 24px;
}
.category-description__title {
    font-family: var(--m-font-body);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--m-on-surface);
    margin: 0 0 16px;
}
.category-description__body {
    font-size: 0.9375rem;
    line-height: 1.75;
    color: var(--m-on-surface-variant, rgba(0,0,0,0.6));
}
.category-description__body :deep(p) {
    margin: 0 0 12px;
}
.category-description__body :deep(p:last-child) {
    margin-bottom: 0;
}
.category-description__body :deep(h3) {
    font-size: 1.0625rem;
    font-weight: 600;
    color: var(--m-on-surface);
    margin: 20px 0 8px;
}

.border-b {
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.subcategory-chips {
    display: flex;
    flex-wrap: nowrap;
    gap: var(--m-space-2);
    overflow-x: auto;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 4px;
}
.subcategory-chips::-webkit-scrollbar { display: none; }
.subcategory-chips :deep(.m-chip) { flex-shrink: 0; white-space: nowrap; }
.chip--active {
    background: var(--m-primary) !important;
    color: var(--m-on-primary) !important;
    border-color: var(--m-primary) !important;
}

.breadcrumbs-compact {
    padding: 4px 0 !important;
    min-height: unset !important;
}

.filter-btn {
    height: 36px !important;
    font-size: 0.8125rem;
}

.filter-btn :deep(.v-btn__overlay) {
    background-color: transparent !important;
}

.filter-btn:focus-visible,
.filter-btn :deep(button:focus-visible) {
    outline-color: var(--m-primary) !important;
}

.filter-btn__range {
    font-size: 0.75rem;
    opacity: 0.85;
    max-width: 140px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.price-slider :deep(.v-slider-track__background) {
    background-color: rgba(var(--v-theme-primary), 0.25) !important;
    opacity: 1 !important;
}

.view-toggle {
    display: inline-flex;
    border: 1px solid var(--m-border);
    border-radius: var(--m-radius);
    overflow: hidden;
    height: 32px;
}
.view-toggle__btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 100%;
    padding: 0;
    background: var(--m-surface);
    border: none;
    cursor: pointer;
    color: var(--m-on-surface-variant);
    transition: all 0.15s ease;
}
.view-toggle__btn:not(:last-child) {
    border-right: 1px solid var(--m-border);
}
.view-toggle__btn:hover {
    background: var(--m-surface-variant);
}
.view-toggle__btn--active {
    background: var(--m-primary);
    color: var(--m-on-primary);
}
.view-toggle__btn--active:hover {
    background: var(--m-primary);
}
</style>
